import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import { 
  QrCode, 
  Barcode, 
  Printer, 
  Download, 
  Search, 
  Package, 
  MapPin,
  Settings,
  ChevronDown,
  Check,
  X,
  RefreshCw,
  Layers,
  Eye,
  Info,
  Lock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const EtiquetasPage = () => {
  const { user, isSuperAdmin, hasPermission } = useAuth();
  const [activeTab, setActiveTab] = useState('equipos');
  const [codeType, setCodeType] = useState('qr');
  const [labelSize, setLabelSize] = useState('medium');
  
  // Permission checks
  const canGenerate = hasPermission('generate_etiquetas');
  const canPrint = hasPermission('print_etiquetas');
  const canConfigure = hasPermission('configure_barcode_scanner');
  
  // Data
  const [equipment, setEquipment] = useState([]);
  const [units, setUnits] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [generatedCodes, setGeneratedCodes] = useState([]);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [categories, setCategories] = useState([]);
  
  // Config
  const [config, setConfig] = useState({
    enabled: false,
    scan_locations: true,
    scan_equipment: true,
    auto_add_on_scan: true,
    show_equipment_history: true,
    label_size: 'medium',
    include_logo: true
  });
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  
  // Preview
  const [previewItem, setPreviewItem] = useState(null);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  
  // Loading
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    loadData();
    loadConfig();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [eqRes, unitsRes, locRes, catRes] = await Promise.all([
        api.get('/equipment'),
        api.get('/equipment-units'),
        api.get('/locations'),
        api.get('/categories')
      ]);
      setEquipment(eqRes.data || []);
      // equipment-units returns {items: [], total: number}
      setUnits(unitsRes.data?.items || unitsRes.data || []);
      setLocations((locRes.data || []).filter(l => !l.is_deleted));
      setCategories((catRes.data || []).filter(c => !c.is_deleted));
    } catch (error) {
      toast.error('Error al cargar datos');
    }
    setLoading(false);
  };

  const loadConfig = async () => {
    try {
      const res = await api.get('/barcode/config');
      setConfig(res.data);
    } catch (error) {
      console.error('Error loading config:', error);
    }
  };

  const saveConfig = async () => {
    try {
      await api.post('/barcode/config', config);
      toast.success('Configuración guardada');
      setShowConfigDialog(false);
    } catch (error) {
      toast.error('Error al guardar configuración');
    }
  };

  const getFilteredItems = () => {
    let items = [];
    
    if (activeTab === 'equipos') {
      items = equipment.map(eq => ({
        id: eq.id,
        type: 'equipment',
        name: eq.name,
        subtitle: `${eq.brand || ''} ${eq.model || ''}`.trim() || 'Sin marca/modelo',
        category: eq.category_name,
        extra: `${eq.total_units || 0} unidades`
      }));
    } else if (activeTab === 'unidades') {
      items = units.map(u => {
        const eq = equipment.find(e => e.id === u.equipment_id);
        return {
          id: u.id,
          type: 'unit',
          name: eq?.name || 'Equipo',
          subtitle: u.serial_number || 'Sin serial',
          category: eq?.category_name,
          extra: u.location_name || 'Sin ubicación',
          status: u.status
        };
      });
    } else if (activeTab === 'ubicaciones') {
      items = locations.map(loc => ({
        id: loc.id,
        type: 'location',
        name: loc.name,
        subtitle: loc.description || 'Sin descripción',
        category: null,
        extra: `${(units || []).filter(u => u.location_id === loc.id).length} equipos`
      }));
    }
    
    // Apply filters
    if (searchTerm) {
      items = items.filter(item => 
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.subtitle.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (categoryFilter !== 'all' && activeTab !== 'ubicaciones') {
      items = items.filter(item => item.category === categoryFilter);
    }
    
    return items;
  };

  const toggleItemSelection = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const selectAll = () => {
    const filtered = getFilteredItems();
    setSelectedItems(filtered.map(item => item.id));
  };

  const deselectAll = () => {
    setSelectedItems([]);
  };

  const generateCodes = async () => {
    if (!canGenerate) {
      toast.error('No tienes permiso para generar códigos');
      return;
    }
    if (selectedItems.length === 0) {
      toast.error('Selecciona al menos un elemento');
      return;
    }
    
    setGenerating(true);
    try {
      const entityType = activeTab === 'equipos' ? 'equipment' : 
                         activeTab === 'unidades' ? 'unit' : 'location';
      
      const res = await api.post('/barcode/batch-generate', {
        entity_type: entityType,
        entity_ids: selectedItems,
        code_type: codeType
      });
      
      setGeneratedCodes(res.data.codes || []);
      toast.success(`${res.data.total} códigos generados`);
    } catch (error) {
      toast.error('Error al generar códigos');
    }
    setGenerating(false);
  };

  const previewCode = async (item) => {
    try {
      const entityType = item.type;
      const endpoint = entityType === 'equipment' ? `/barcode/equipment/${item.id}` :
                       entityType === 'unit' ? `/barcode/unit/${item.id}` :
                       `/barcode/location/${item.id}`;
      
      const res = await api.get(`${endpoint}?code_type=${codeType}`);
      setPreviewItem({
        ...res.data,
        itemData: item
      });
      setShowPreviewDialog(true);
    } catch (error) {
      toast.error('Error al generar vista previa');
    }
  };

  const printLabels = () => {
    if (generatedCodes.length === 0) {
      toast.error('Genera códigos primero');
      return;
    }
    
    const printWindow = window.open('', '_blank');
    
    const labelWidth = labelSize === 'small' ? '45mm' : labelSize === 'medium' ? '60mm' : '80mm';
    const labelHeight = labelSize === 'small' ? '25mm' : labelSize === 'medium' ? '35mm' : '50mm';
    const fontSize = labelSize === 'small' ? '7px' : labelSize === 'medium' ? '9px' : '11px';
    const imgSize = labelSize === 'small' ? '20mm' : labelSize === 'medium' ? '28mm' : '40mm';
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Etiquetas CM Idiomas</title>
        <style>
          @page { 
            size: auto; 
            margin: 5mm;
          }
          body { 
            font-family: Arial, sans-serif; 
            margin: 0; 
            padding: 10px;
          }
          .labels-container {
            display: flex;
            flex-wrap: wrap;
            gap: 5mm;
            justify-content: flex-start;
          }
          .label {
            width: ${labelWidth};
            height: ${labelHeight};
            border: 1px solid #ddd;
            border-radius: 4px;
            padding: 3mm;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: space-between;
            box-sizing: border-box;
            page-break-inside: avoid;
            background: white;
          }
          .label-header {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 2mm;
            border-bottom: 1px solid #00B2A9;
            padding-bottom: 2mm;
            margin-bottom: 1mm;
          }
          .label-logo {
            font-weight: bold;
            color: #00B2A9;
            font-size: ${fontSize};
          }
          .label-code {
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .label-code img {
            max-width: ${imgSize};
            max-height: ${imgSize};
            object-fit: contain;
          }
          .label-info {
            width: 100%;
            text-align: center;
            font-size: ${fontSize};
            line-height: 1.2;
          }
          .label-name {
            font-weight: bold;
            color: #333;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .label-subtitle {
            color: #666;
            font-size: calc(${fontSize} - 1px);
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        <div class="labels-container">
          ${generatedCodes.map(code => `
            <div class="label">
              ${config.include_logo ? `
                <div class="label-header">
                  <span class="label-logo">CM IDIOMAS</span>
                </div>
              ` : ''}
              <div class="label-code">
                <img src="data:image/png;base64,${code.image_base64}" alt="Code" />
              </div>
              <div class="label-info">
                <div class="label-name">${code.info?.name || code.info?.serial_number || code.info?.equipment_name || 'N/A'}</div>
                <div class="label-subtitle">${code.info?.serial_number || code.info?.brand || code.info?.description || ''}</div>
              </div>
            </div>
          `).join('')}
        </div>
        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const downloadCodes = () => {
    if (generatedCodes.length === 0) {
      toast.error('Genera códigos primero');
      return;
    }
    
    // Download as individual images
    generatedCodes.forEach((code, index) => {
      const link = document.createElement('a');
      link.href = `data:image/png;base64,${code.image_base64}`;
      link.download = `${code.entity_type}_${code.entity_id}_${code.code_type}.png`;
      link.click();
    });
    
    toast.success(`${generatedCodes.length} imágenes descargadas`);
  };

  const filteredItems = getFilteredItems();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Etiquetas QR/Códigos de Barras</h1>
          <p className="text-muted-foreground text-sm">
            Genera e imprime etiquetas para equipos y ubicaciones
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canConfigure && (
            <Button 
              variant="outline" 
              onClick={() => setShowConfigDialog(true)}
              data-testid="barcode-config-btn"
            >
              <Settings className="h-4 w-4 mr-2" />
              Configuración
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Panel - Selection */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Seleccionar elementos</CardTitle>
                <div className="flex items-center gap-2">
                  {/* Code Type Selector */}
                  <Select value={codeType} onValueChange={setCodeType}>
                    <SelectTrigger className="w-32" data-testid="code-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="qr">
                        <div className="flex items-center gap-2">
                          <QrCode className="h-4 w-4" />
                          QR Code
                        </div>
                      </SelectItem>
                      <SelectItem value="barcode">
                        <div className="flex items-center gap-2">
                          <Barcode className="h-4 w-4" />
                          Código de Barras
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Tabs */}
              <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setSelectedItems([]); }}>
                <TabsList className="grid grid-cols-3 mb-4">
                  <TabsTrigger value="equipos" data-testid="tab-equipos">
                    <Package className="h-4 w-4 mr-2" />
                    Equipos
                  </TabsTrigger>
                  <TabsTrigger value="unidades" data-testid="tab-unidades">
                    <Layers className="h-4 w-4 mr-2" />
                    Unidades
                  </TabsTrigger>
                  <TabsTrigger value="ubicaciones" data-testid="tab-ubicaciones">
                    <MapPin className="h-4 w-4 mr-2" />
                    Ubicaciones
                  </TabsTrigger>
                </TabsList>

                {/* Filters */}
                <div className="flex flex-wrap gap-3 mb-4">
                  <div className="flex-1 min-w-[200px]">
                    <Input
                      placeholder="Buscar..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full"
                      data-testid="search-input"
                    />
                  </div>
                  {activeTab !== 'ubicaciones' && (
                    <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="Categoría" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas</SelectItem>
                        {categories.map(cat => (
                          <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={selectAll}>
                      Seleccionar todo
                    </Button>
                    <Button variant="outline" size="sm" onClick={deselectAll}>
                      Deseleccionar
                    </Button>
                  </div>
                </div>

                {/* Items List */}
                <div className="border rounded-lg max-h-[400px] overflow-y-auto">
                  {loading ? (
                    <div className="p-8 text-center text-muted-foreground">
                      <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                      Cargando...
                    </div>
                  ) : filteredItems.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground">
                      No se encontraron elementos
                    </div>
                  ) : (
                    <div className="divide-y divide-border">
                      {filteredItems.map(item => (
                        <div 
                          key={item.id}
                          className={`flex items-center gap-3 p-3 hover:bg-muted/50 cursor-pointer transition-colors ${
                            selectedItems.includes(item.id) ? 'bg-primary/10' : ''
                          }`}
                          onClick={() => toggleItemSelection(item.id)}
                          data-testid={`item-${item.id}`}
                        >
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            selectedItems.includes(item.id) 
                              ? 'bg-primary border-primary' 
                              : 'border-muted-foreground/30'
                          }`}>
                            {selectedItems.includes(item.id) && (
                              <Check className="h-3 w-3 text-white" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{item.name}</div>
                            <div className="text-sm text-muted-foreground truncate">
                              {item.subtitle}
                            </div>
                          </div>
                          <div className="text-xs text-muted-foreground text-right">
                            {item.category && (
                              <div className="bg-muted px-2 py-0.5 rounded mb-1">{item.category}</div>
                            )}
                            <div>{item.extra}</div>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => { e.stopPropagation(); previewCode(item); }}
                            data-testid={`preview-${item.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Selection Summary */}
                {selectedItems.length > 0 && (
                  <div className="mt-4 p-3 bg-primary/10 rounded-lg flex items-center justify-between">
                    <span className="text-sm font-medium">
                      {selectedItems.length} elemento(s) seleccionado(s)
                    </span>
                    <Button 
                      onClick={generateCodes}
                      disabled={generating}
                      data-testid="generate-codes-btn"
                    >
                      {generating ? (
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      ) : codeType === 'qr' ? (
                        <QrCode className="h-4 w-4 mr-2" />
                      ) : (
                        <Barcode className="h-4 w-4 mr-2" />
                      )}
                      Generar códigos
                    </Button>
                  </div>
                )}
              </Tabs>
            </CardContent>
          </Card>
        </div>

        {/* Right Panel - Generated Codes */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Códigos generados</CardTitle>
                {generatedCodes.length > 0 && (
                  <span className="text-sm text-muted-foreground">
                    {generatedCodes.length} códigos
                  </span>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {generatedCodes.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <QrCode className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">Selecciona elementos y genera códigos</p>
                </div>
              ) : (
                <>
                  {/* Label Size Selector */}
                  <div className="mb-4">
                    <Label className="text-sm mb-2 block">Tamaño de etiqueta</Label>
                    <Select value={labelSize} onValueChange={setLabelSize}>
                      <SelectTrigger data-testid="label-size-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="small">Pequeña (45x25mm)</SelectItem>
                        <SelectItem value="medium">Mediana (60x35mm)</SelectItem>
                        <SelectItem value="large">Grande (80x50mm)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Preview Grid */}
                  <div className="grid grid-cols-2 gap-2 max-h-[300px] overflow-y-auto mb-4">
                    {generatedCodes.slice(0, 8).map((code, idx) => (
                      <div 
                        key={idx}
                        className="border rounded p-2 text-center bg-white"
                      >
                        <img 
                          src={`data:image/png;base64,${code.image_base64}`}
                          alt={code.code_data}
                          className="w-full h-auto max-h-20 object-contain mb-1"
                        />
                        <div className="text-[10px] text-muted-foreground truncate">
                          {code.info?.name || code.info?.serial_number || 'N/A'}
                        </div>
                      </div>
                    ))}
                  </div>
                  {generatedCodes.length > 8 && (
                    <p className="text-xs text-center text-muted-foreground mb-4">
                      +{generatedCodes.length - 8} más...
                    </p>
                  )}

                  {/* Action Buttons */}
                  <div className="space-y-2">
                    <Button 
                      className="w-full" 
                      onClick={printLabels}
                      disabled={!canPrint}
                      data-testid="print-labels-btn"
                    >
                      {!canPrint && <Lock className="h-4 w-4 mr-2" />}
                      {canPrint && <Printer className="h-4 w-4 mr-2" />}
                      Imprimir etiquetas
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={downloadCodes}
                      disabled={!canGenerate}
                      data-testid="download-codes-btn"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Descargar imágenes
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Info Card */}
          <Card className="mt-4">
            <CardContent className="pt-4">
              <div className="flex gap-3">
                <Info className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <div className="text-sm text-muted-foreground">
                  <p className="font-medium text-foreground mb-1">Tipos de código</p>
                  <ul className="space-y-1">
                    <li><strong>EQ:</strong> Tipo de equipo</li>
                    <li><strong>UN:</strong> Unidad específica</li>
                    <li><strong>LOC:</strong> Ubicación</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Configuration Dialog */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Configuración del escáner</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Escáner habilitado</Label>
                <p className="text-xs text-muted-foreground">
                  Activa el escáner en la página de Movimientos
                </p>
              </div>
              <Switch
                checked={config.enabled}
                onCheckedChange={(v) => setConfig({...config, enabled: v})}
                data-testid="scanner-enabled-switch"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label>Escanear ubicaciones</Label>
                <p className="text-xs text-muted-foreground">
                  Permite escanear QR de ubicaciones
                </p>
              </div>
              <Switch
                checked={config.scan_locations}
                onCheckedChange={(v) => setConfig({...config, scan_locations: v})}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label>Escanear equipos</Label>
                <p className="text-xs text-muted-foreground">
                  Permite escanear QR de equipos/unidades
                </p>
              </div>
              <Switch
                checked={config.scan_equipment}
                onCheckedChange={(v) => setConfig({...config, scan_equipment: v})}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label>Agregar automáticamente</Label>
                <p className="text-xs text-muted-foreground">
                  Agrega equipo al escanear sin confirmación
                </p>
              </div>
              <Switch
                checked={config.auto_add_on_scan}
                onCheckedChange={(v) => setConfig({...config, auto_add_on_scan: v})}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label>Mostrar historial</Label>
                <p className="text-xs text-muted-foreground">
                  Muestra historial del equipo al escanearlo
                </p>
              </div>
              <Switch
                checked={config.show_equipment_history}
                onCheckedChange={(v) => setConfig({...config, show_equipment_history: v})}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Incluir logo en etiquetas</Label>
                <p className="text-xs text-muted-foreground">
                  Agrega "CM IDIOMAS" en las etiquetas
                </p>
              </div>
              <Switch
                checked={config.include_logo}
                onCheckedChange={(v) => setConfig({...config, include_logo: v})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={saveConfig} data-testid="save-config-btn">
              Guardar configuración
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Vista previa</DialogTitle>
          </DialogHeader>
          {previewItem && (
            <div className="text-center py-4">
              <div className="bg-white p-4 rounded-lg border inline-block mb-4">
                <img 
                  src={`data:image/png;base64,${previewItem.image_base64}`}
                  alt="Preview"
                  className="max-w-[200px] h-auto"
                />
              </div>
              <div className="text-sm space-y-1">
                <p className="font-medium">{previewItem.itemData?.name}</p>
                <p className="text-muted-foreground">{previewItem.itemData?.subtitle}</p>
                <p className="text-xs text-muted-foreground font-mono">{previewItem.code_data}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EtiquetasPage;
